/**
 * The app's formatters — one per kind of value (UX-095, UX-099, UX-142).
 *
 * House style (docs/product/ux-voice.md):
 *   money      $12,840.00 everywhere a person reads an amount
 *              $74.6K only on KPI tiles / charts, with the full value in a tooltip
 *   quantity   2,800 sq ft · 12 lf · 3 ea
 *   phone      (205) 555-0123 — never "+12055550123"
 *   schedule   Tue, Oct 20 · 9:00 AM      all-day: Tue, Oct 20 · All day
 *   document   Oct 20, 2026
 *   relative   "2 hours ago" — activity feeds only
 *
 * Never use `toFixed()` / string templates for money in components.
 */
import { formatInTimeZone } from 'date-fns-tz'
import { formatDistanceToNow } from 'date-fns'
import { getWorkspaceTimezone } from '@/lib/workspace'
import { addDaysDateOnly, formatDateOnly, isDateOnly, toDateOnly } from '@/lib/dateOnly'

export { formatDateOnly, toDateOnly, dateOnlyForApi, todayDateOnly, isPastDateOnly } from '@/lib/dateOnly'

const EMPTY = '—'

// ─── numbers & money ────────────────────────────────────────────────────────

/** Parse numbers, numeric strings (Prisma Decimal comes as a string) and null. */
export function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[$,\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const moneyWholeFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })

/**
 * "$1,284.00". Always two decimals unless `{ cents: false }` (whole-dollar
 * inputs like a budget slider). Missing values render as "—", never "$0.00".
 */
export function formatMoney(value: unknown, opts: { cents?: boolean; fallback?: string } = {}): string {
  const n = toNumber(value)
  if (n == null) return opts.fallback ?? EMPTY
  // Avoid "-$0.00" from floating-point dust.
  const v = Math.abs(n) < 0.005 ? 0 : n
  return (opts.cents === false ? moneyWholeFmt : moneyFmt).format(v)
}

/**
 * "$48K", "$1.2M", "$950" — KPI tiles and chart axes ONLY. Pair it with the
 * full value: `<Money value={x} compact />` renders the tooltip for you.
 */
export function formatMoneyCompact(value: unknown, opts: { fallback?: string } = {}): string {
  const n = toNumber(value)
  if (n == null) return opts.fallback ?? EMPTY
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  const trim = (x: number) => x.toFixed(1).replace(/\.0$/, '')
  if (abs >= 1_000_000) return `${sign}$${trim(abs / 1_000_000)}M`
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1_000)}K`
  if (abs >= 1_000) return `${sign}$${trim(abs / 1_000)}K`
  return `${sign}${moneyWholeFmt.format(abs)}`
}

/** "1,284" · "2.5" · "0.75" — thousands separators, up to 2 decimals. */
export function formatNumber(value: unknown, opts: { maxDecimals?: number; fallback?: string } = {}): string {
  const n = toNumber(value)
  if (n == null) return opts.fallback ?? EMPTY
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: opts.maxDecimals ?? 2 }).format(n)
}

/** "12%" / "12.5%" from a percentage number (12.5), not a ratio. */
export function formatPercent(value: unknown, opts: { maxDecimals?: number; fallback?: string } = {}): string {
  const n = toNumber(value)
  if (n == null) return opts.fallback ?? EMPTY
  return `${formatNumber(n, { maxDecimals: opts.maxDecimals ?? 1 })}%`
}

const UNIT_LABELS: Record<string, { one: string; many: string }> = {
  ea: { one: 'ea', many: 'ea' },
  each: { one: 'ea', many: 'ea' },
  unit: { one: 'ea', many: 'ea' },
  sqft: { one: 'sq ft', many: 'sq ft' },
  'sq ft': { one: 'sq ft', many: 'sq ft' },
  sf: { one: 'sq ft', many: 'sq ft' },
  lf: { one: 'lf', many: 'lf' },
  'lin ft': { one: 'lf', many: 'lf' },
  ft: { one: 'ft', many: 'ft' },
  hr: { one: 'hr', many: 'hrs' },
  hour: { one: 'hr', many: 'hrs' },
  hours: { one: 'hr', many: 'hrs' },
  day: { one: 'day', many: 'days' },
  days: { one: 'day', many: 'days' },
  gal: { one: 'gal', many: 'gal' },
  lot: { one: 'lot', many: 'lots' },
  job: { one: 'job', many: 'jobs' },
}

/** Display label for a unit code ("sqft" → "sq ft"). Unknown units pass through. */
export function unitLabel(unit: string | null | undefined, qty = 2): string {
  if (!unit) return ''
  const key = unit.trim().toLowerCase()
  const entry = UNIT_LABELS[key]
  if (!entry) return unit.trim()
  return qty === 1 ? entry.one : entry.many
}

const PER_UNIT_WORDS: Record<string, string> = {
  ea: 'each', lf: 'per linear foot', 'sq ft': 'per sq ft', ft: 'per foot',
  hr: 'per hour', day: 'per day', gal: 'per gallon', lot: 'per lot', job: 'per job',
}

/**
 * How a price reads in words: "each", "per linear foot", "per hour" (UX-109:
 * the price book said "per lf" and "per ea"). Unknown units: "per {unit}".
 */
export function perUnitLabel(unit: string | null | undefined): string {
  const one = unitLabel(unit, 1)
  if (!one) return 'each'
  return PER_UNIT_WORDS[one] ?? `per ${one}`
}

/** "2,800 sq ft" · "12 lf" · "1 hr" · "2.5 hrs". */
export function formatQty(qty: unknown, unit?: string | null): string {
  const n = toNumber(qty)
  if (n == null) return EMPTY
  const label = unitLabel(unit, n)
  return label ? `${formatNumber(n)} ${label}` : formatNumber(n)
}

const COUNT_UNITS = new Set(['', 'ea', 'each', 'unit', 'pc'])

/** Unit price read against the line's unit: "$3.25 per sq ft", "$38.00 per lf", "$85.00 each" (UX-124). */
export function formatRate(unitPrice: unknown, unit?: string | null): string {
  const money = formatMoney(unitPrice)
  const key = (unit ?? '').trim().toLowerCase()
  return COUNT_UNITS.has(key) ? `${money} each` : `${money} per ${unitLabel(unit, 1)}`
}

// ─── phone ──────────────────────────────────────────────────────────────────

/**
 * US display format "(205) 555-0123" from anything stored or typed
 * ("+12055550123", "205.555.0123", "2055550123"). Non-US / partial numbers are
 * returned trimmed as typed. Empty → "".
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits
  if (ten.length === 10 && (raw.trim().startsWith('+1') || !raw.trim().startsWith('+'))) {
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
  }
  return raw.trim()
}

/**
 * An address as the lines it prints on: the owner's own line breaks, else
 * "street" / "city, state zip" split at the first comma. Same rule as the
 * server's comms/format addressLines, so a preview matches the sent PDF.
 */
export function addressLines(addr: string | null | undefined): string[] {
  const a = (addr ?? '').normalize('NFC').trim()
  if (!a) return []
  if (a.includes('\n')) return a.split(/\s*\n\s*/).filter(Boolean)
  const i = a.indexOf(',')
  if (i <= 0) return [a]
  const rest = a.slice(i + 1).trim()
  return rest ? [a.slice(0, i).trim(), rest] : [a.slice(0, i).trim()]
}

/**
 * An address field's example in the business's own town: "e.g. 1420 Oak St,
 * Birmingham, AL" when the business address is "…\nBirmingham, AL 35203".
 * Never another town (UX-122); without an address it is just the street.
 */
export function addressExample(businessAddress: string | null | undefined): string {
  const lines = addressLines(businessAddress)
  const town = lines.length > 1 ? lines[lines.length - 1].replace(/\s+\d{5}(-\d{4})?$/, '').trim() : ''
  return town ? `e.g. 1420 Oak St, ${town}` : 'e.g. 1420 Oak St'
}

/**
 * Progressive formatting for a phone <input> while typing: "205" → "(205",
 * "2055550" → "(205) 555-0". Use as the controlled value; store
 * `normalizeUsPhone()` (utils/phone) on save.
 */
export function formatPhoneInput(typed: string): string {
  if (typed.trim().startsWith('+') && !typed.trim().startsWith('+1')) return typed
  let d = typed.replace(/\D/g, '')
  if (d.length === 11 && d[0] === '1') d = d.slice(1)
  d = d.slice(0, 10)
  if (d.length === 0) return ''
  if (d.length < 4) return `(${d}`
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** `tel:` href from any stored or typed number. */
export function telHref(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '')
  if (digits.length === 10) return `tel:+1${digits}`
  if (digits.length === 11 && digits[0] === '1') return `tel:+${digits}`
  return `tel:${(raw ?? '').replace(/[^\d+]/g, '')}`
}

// ─── dates & times (workspace time zone) ────────────────────────────────────

type DateInput = string | Date | null | undefined

function tzOf(tz?: string | null): string {
  const candidate = tz || getWorkspaceTimezone() || 'America/Chicago'
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate })
    return candidate
  } catch {
    return 'America/Chicago'
  }
}

function toInstant(input: DateInput | number): Date | null {
  if (input == null || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "9:00 AM" in the workspace zone. */
export function formatClockTime(input: DateInput | number, tz?: string | null): string {
  const d = toInstant(input)
  return d ? formatInTimeZone(d, tzOf(tz), 'h:mm a') : EMPTY
}

/** "Oct 20, 2026" — documents, lists, due dates. Date-only safe. */
export function formatDocDate(input: DateInput, tz?: string | null): string {
  if (typeof input === 'string' && isDateOnly(input)) return formatDateOnly(input)
  const d = toInstant(input)
  return d ? formatInTimeZone(d, tzOf(tz), 'MMM d, yyyy') : EMPTY
}

/**
 * True when a span should read as "All day": the explicit flag, a date-only
 * value, or a span that starts at midnight (workspace zone, or legacy UTC
 * midnight) and ends at a midnight / 23:59 / not at all.
 */
export function isAllDaySpan(start: DateInput, end?: DateInput, opts: { allDay?: boolean | null; tz?: string | null } = {}): boolean {
  if (opts.allDay) return true
  // An explicit false comes from a record that knows it is timed: a 7 PM
  // Central job is 00:00 UTC and must not read as "All day".
  if (opts.allDay === false) return false
  if (typeof start === 'string' && isDateOnly(start)) return true
  const s = toInstant(start)
  if (!s) return false
  const zone = tzOf(opts.tz)
  const isMidnight = (d: Date) => {
    const local = formatInTimeZone(d, zone, 'HH:mm')
    const utc = d.toISOString().slice(11, 16)
    return local === '00:00' || utc === '00:00'
  }
  const isEndOfDay = (d: Date) => {
    const local = formatInTimeZone(d, zone, 'HH:mm')
    const utc = d.toISOString().slice(11, 16)
    return local === '00:00' || local === '23:59' || utc === '00:00' || utc === '23:59'
  }
  if (!isMidnight(s)) return false
  const e = toInstant(end ?? null)
  return !e || isEndOfDay(e)
}

/**
 * Schedule label — the one way to print when something happens.
 *   timed, same day:  "Tue, Oct 20 · 9:00 AM"          (with end: "· 9:00 – 11:30 AM")
 *   timed, multi-day: "Tue, Oct 20, 9:00 AM – Thu, Oct 22, 5:00 PM"
 *   all-day:          "Tue, Oct 20 · All day"
 *   all-day multi:    "Tue, Oct 20 – Thu, Oct 22 · All day"
 * Never invents a clock time for all-day or date-only items (UX-099).
 * `end` for all-day items may be exclusive (next midnight) — handled.
 */
export function formatSchedule(
  start: DateInput,
  end?: DateInput,
  opts: { allDay?: boolean | null; tz?: string | null; withYear?: boolean } = {},
): string {
  const zone = tzOf(opts.tz)
  const dayFmt = opts.withYear ? 'EEE, MMM d, yyyy' : 'EEE, MMM d'
  if (isAllDaySpan(start, end, opts)) {
    const first = toDateOnly(start ?? null, zone)
    if (!first) return EMPTY
    let last = first
    const e = toInstant(end ?? null)
    if (e) {
      // Ends are exclusive (iCal/Google convention: next midnight, or the next
      // day for a date-only end) → step back to the last covered day. A
      // same-day end steps back before `first` and is ignored.
      const candidate = typeof end === 'string' && isDateOnly(end)
        ? addDaysDateOnly(end, -1)
        : toDateOnly(new Date(e.getTime() - 1), zone)
      if (candidate && candidate > first) last = candidate
    }
    const style = opts.withYear ? 'document' : 'weekday'
    const label = (d: string) => (opts.withYear ? `${formatDateOnly(d, 'weekday')}, ${d.slice(0, 4)}` : formatDateOnly(d, style))
    return last === first ? `${label(first)} · All day` : `${label(first)} – ${label(last)} · All day`
  }
  const s = toInstant(start)
  if (!s) return EMPTY
  const day = formatInTimeZone(s, zone, dayFmt)
  const t1 = formatInTimeZone(s, zone, 'h:mm a')
  const e = toInstant(end ?? null)
  if (!e || e.getTime() <= s.getTime()) return `${day} · ${t1}`
  const sameDay = formatInTimeZone(s, zone, 'yyyy-MM-dd') === formatInTimeZone(e, zone, 'yyyy-MM-dd')
  if (sameDay) {
    const t2 = formatInTimeZone(e, zone, 'h:mm a')
    const sameMeridiem = t1.slice(-2) === t2.slice(-2)
    return `${day} · ${sameMeridiem ? t1.slice(0, -3) : t1} – ${t2}`
  }
  return `${day}, ${t1} – ${formatInTimeZone(e, zone, dayFmt)}, ${formatInTimeZone(e, zone, 'h:mm a')}`
}

/** "2 hours ago" — activity feeds only. */
export function formatRelative(input: DateInput): string {
  const d = toInstant(input)
  return d ? formatDistanceToNow(d, { addSuffix: true }) : EMPTY
}
